import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Download, Search, ClipboardList, Utensils } from "lucide-react";
import { toast } from "sonner";

function csvDownload(rows, filename) {
  if (!rows || rows.length === 0) return;
  const headers = ["date", "slot", "collected_at_ist", "student_id", "student_name", "grade", "rfid_card_id", "plan_name", "plan_type", "is_preorder"];
  const csv = [headers.join(",")].concat(
    rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const PLAN_TYPES = [
  { value: "", label: "All plan types" },
  { value: "subscription", label: "Subscription" },
  { value: "preorder", label: "Pre-order" },
  { value: "special", label: "Special Events" },
];

const SLOTS = [
  { value: "", label: "All slots" },
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "snacks", label: "Snacks" },
];

const today = () => new Date().toISOString().slice(0, 10);

const slotPill = (slot) => {
  const map = {
    breakfast: "bg-amber-50 text-amber-700",
    lunch: "bg-emerald-50 text-emerald-700",
    snacks: "bg-violet-50 text-violet-700",
  };
  return map[slot] || "bg-cream-200 text-ink-700";
};

export default function AdminConsumptionLog() {
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [planType, setPlanType] = useState("");
  const [slot, setSlot] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    const q = new URLSearchParams();
    if (startDate) q.set("startDate", startDate);
    if (endDate) q.set("endDate", endDate);
    if (planType) q.set("planType", planType);
    if (slot) q.set("slot", slot);
    setLoading(true);
    api.get(`/rfid-monitor/consumption-log?${q.toString()}`)
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load consumption log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate, planType, slot]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.student_name || "").toLowerCase().includes(s)
      || (r.student_id || "").toLowerCase().includes(s)
      || (r.rfid_card_id || "").toLowerCase().includes(s)
      || (r.plan_name || "").toLowerCase().includes(s)
      || (r.grade || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const uniqueStudents = new Set(filtered.map(r => r.student_id || r.student_name)).size;
    const preorder = filtered.filter(r => r.is_preorder).length;
    const subscription = filtered.length - preorder;
    return { total: filtered.length, uniqueStudents, preorder, subscription };
  }, [filtered]);

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Reports</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2 flex items-center gap-3">
              <ClipboardList size={32} className="text-brand" /> Daily Consumption Log
            </h1>
            <p className="text-sm text-ink-500 mt-1">Every student that tapped their RFID card and ate, with date and plan-type filters.</p>
          </div>
          <button data-testid="consumption-csv" onClick={() => csvDownload(filtered, `consumption-${startDate}_to_${endDate}.csv`)}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-border p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">From</label>
            <input data-testid="consumption-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">To</label>
            <input data-testid="consumption-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Plan type</label>
            <select data-testid="consumption-plan-type" value={planType} onChange={e => setPlanType(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none bg-white">
              {PLAN_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Slot</label>
            <select data-testid="consumption-slot" value={slot} onChange={e => setSlot(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none bg-white">
              {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
              <input data-testid="consumption-search" type="text" placeholder="Name, student ID, RFID…"
                value={search} onChange={e => setSearch(e.target.value)}
                className="block w-full rounded-xl border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Total taps</div>
            <div className="font-heading text-3xl font-extrabold mt-2" data-testid="stat-total-taps">{stats.total}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Unique students</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-emerald-700" data-testid="stat-unique-students">{stats.uniqueStudents}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Pre-order taps</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-violet-700" data-testid="stat-preorder">{stats.preorder}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Subscription taps</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-brand" data-testid="stat-subscription">{stats.subscription}</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid="consumption-table">
          <table className="w-full text-sm">
            <thead className="bg-cream-200">
              <tr>
                <th className="p-3 text-left text-xs uppercase font-bold">Date</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Time (IST)</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Slot</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Student</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Student ID</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Grade</th>
                <th className="p-3 text-left text-xs uppercase font-bold">RFID</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Plan</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Type</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-8 text-center text-ink-500">Loading…</td></tr>
              )}
              {!loading && filtered.map((r, idx) => (
                <tr key={r.id || idx} className="border-t border-border hover:bg-cream-100" data-testid={`consumption-row-${idx}`}>
                  <td className="p-3 font-semibold">{r.date}</td>
                  <td className="p-3 text-ink-500">{r.collected_at_ist || "—"}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${slotPill(r.slot)}`}>
                      <Utensils size={11} /> {r.slot}
                    </span>
                  </td>
                  <td className="p-3 font-semibold">{r.student_name}</td>
                  <td className="p-3 text-ink-500">{r.student_id || "—"}</td>
                  <td className="p-3 text-ink-500">{r.grade || "—"}</td>
                  <td className="p-3 text-ink-500 font-mono text-xs">{r.rfid_card_id || "—"}</td>
                  <td className="p-3">{r.plan_name || "—"}</td>
                  <td className="p-3">
                    {r.plan_family ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${
                        r.plan_family === "preorder" ? "bg-violet-50 text-violet-700"
                        : r.plan_family === "special" ? "bg-rose-50 text-rose-700"
                        : "bg-brand-50 text-brand"
                      }`}>
                        {r.plan_family === "preorder" ? "Pre-order" : r.plan_family === "special" ? "Special" : "Subscription"}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="p-12 text-center text-ink-500">
                  No consumption recorded for the selected filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
